import type { Service } from '../../api/client'
import type { Discount, DiscountInput } from '../../types/app'
import { formatPrice, parsePrice } from '../../catalog/format'
import { liveLengthIds } from './packageForm'

export interface OfferForm {
  name: string
  type: 'sale' | 'code'
  code: string
  unit: 'percent' | 'amount'
  value: string // a whole percent, or pounds ("10.50")
  startsOn: string
  endsOn: string
  allServices: boolean // false = only lengthIds; an empty list is then an error
  lengthIds: string[]
  maxUses: string
  oncePerClient: boolean
}

export function emptyOfferForm(today: string): OfferForm {
  return {
    name: '',
    type: 'sale',
    code: '',
    unit: 'percent',
    value: '',
    startsOn: today,
    endsOn: '',
    allServices: true,
    lengthIds: [],
    maxUses: '',
    oncePerClient: false,
  }
}

export function formFromOffer(d: Discount): OfferForm {
  return {
    name: d.name,
    type: d.code ? 'code' : 'sale',
    code: d.code ?? '',
    unit: d.kind,
    value: d.kind === 'percent' ? String(d.value) : (d.value / 100).toFixed(2),
    startsOn: d.starts_on,
    endsOn: d.ends_on ?? '',
    allServices: d.all_services,
    lengthIds: [...d.service_length_ids],
    maxUses: d.max_uses === null ? '' : String(d.max_uses),
    oncePerClient: d.once_per_client,
  }
}

// lockedForm is the form for an offer bookings already carry: its saved terms
// with only the name she typed, so the next save sends the terms unchanged.
export function lockedForm(d: Discount, typedName: string): OfferForm {
  return { ...formFromOffer(d), name: typedName }
}

const CODE_PATTERN = /^[A-Z0-9]{3,20}$/

const normalCode = (code: string) => code.trim().toUpperCase()

// percentOf and pencePerAmount read the value field; null when it is not a
// usable number.
function percentOf(value: string): number | null {
  return /^\d{1,3}$/.test(value.trim()) ? Number(value.trim()) : null
}

function maxUsesOf(value: string): number | null {
  return /^\d{1,6}$/.test(value.trim()) ? Number(value.trim()) : null
}

// validateOfferForm spots what it can without asking the API, which still
// checks every rule. Keys are the API's field names.
// When locked, only the name can change, so only the name is checked.
export function validateOfferForm(f: OfferForm, locked = false, services?: Service[]): Record<string, string> {
  const errors: Record<string, string> = {}

  if (f.name.trim() === '') {
    errors.name = 'Give the offer a name.'
  }
  if (locked) {
    return errors
  }

  if (f.unit === 'percent') {
    const percent = percentOf(f.value)
    if (percent === null || percent < 1 || percent > 100) {
      errors.value = 'Choose a percentage between 1 and 100.'
    }
  } else {
    const pence = parsePrice(f.value)
    if (pence === null || pence < 1) {
      errors.value = 'Enter an amount of at least 1p.'
    }
  }

  if (f.type === 'code' && !CODE_PATTERN.test(normalCode(f.code))) {
    errors.code = 'Codes use letters and numbers, 3–20 characters.'
  }

  if (f.startsOn === '') {
    errors.starts_on = 'Choose a start date.'
  }
  if (f.endsOn === '') {
    if (f.type === 'sale') {
      errors.ends_on = 'A sale needs an end date.'
    }
  } else if (f.startsOn !== '' && f.endsOn < f.startsOn) {
    errors.ends_on = 'The end date is before the start date.'
  }

  if (f.type === 'code' && f.maxUses.trim() !== '') {
    const n = maxUsesOf(f.maxUses)
    if (n === null || n < 1) {
      errors.max_uses = 'Enter a limit of at least 1.'
    }
  }

  if (!f.allServices && (services ? liveLengthIds(services, f.lengthIds) : f.lengthIds).length === 0) {
    errors.service_ids = 'Choose at least one service.'
  }

  return errors
}

// toDiscountInput builds the request body; call it once validateOfferForm
// finds nothing wrong.
export function toDiscountInput(f: OfferForm, services?: Service[]): DiscountInput {
  const isCode = f.type === 'code'
  const value = f.unit === 'percent' ? (percentOf(f.value) ?? 0) : (parsePrice(f.value) ?? 0)
  return {
    name: f.name.trim(),
    code: isCode ? normalCode(f.code) : null,
    kind: f.unit,
    value,
    starts_on: f.startsOn,
    ends_on: f.endsOn === '' ? null : f.endsOn,
    service_length_ids: f.allServices ? [] : services ? liveLengthIds(services, f.lengthIds) : f.lengthIds,
    max_uses: isCode && f.maxUses.trim() !== '' ? maxUsesOf(f.maxUses) : null,
    once_per_client: isCode ? f.oncePerClient : false,
  }
}

export function offerSummary(d: Discount): string {
  return d.kind === 'percent' ? `${d.value}% off` : `${formatPrice(d.value)} off`
}

// usesLabel is for codes only; a sale has no use count to show.
export function usesLabel(d: Discount): string | null {
  if (!d.code) {
    return null
  }
  return d.max_uses === null ? `${d.uses} used` : `${d.uses} of ${d.max_uses} used`
}
